import {
  GETTING_STARTED_TASK_IDS,
  type GettingStartedTaskId,
} from "@jurni/schemas";
import type { LucideIcon } from "lucide-react";
import {
  integrationDetailHref,
  integrationsHref,
} from "@/lib/integrations-href";
import {
  BarChart3,
  Code2,
  Filter,
  FlaskConical,
  Globe,
  Link2,
  Megaphone,
  MessageSquareQuote,
  Palette,
  Sparkles,
  Target,
  Users,
} from "lucide-react";

export type GettingStartedParentId = "account-setup" | "first-creating";

export interface GettingStartedSection {
  id: GettingStartedParentId;
  label: string;
}

export interface GettingStartedTask {
  id: GettingStartedTaskId;
  parentId: GettingStartedParentId;
  label: string;
  href: string;
  // The id of the element the task scrolls to on its page; tasks that open a dedicated page have none.
  scrollTo?: string;
  icon: LucideIcon;
}

export const GETTING_STARTED_SECTIONS: readonly GettingStartedSection[] = [
  { id: "account-setup", label: "Account Setup" },
  { id: "first-creating", label: "Start Building" },
] as const;

export const GETTING_STARTED_TASKS_BY_ID: Record<
  GettingStartedTaskId,
  Omit<GettingStartedTask, "id">
> = {
  "jurni-core-script": {
    parentId: "account-setup",
    label: "Activate Jurni Core Script",
    href: "/settings",
    scrollTo: "jurni-script",
    icon: Code2,
  },
  "tracking-pixels": {
    parentId: "account-setup",
    label: "Configure Tracking Pixels",
    href: integrationsHref({ tab: "data-analytics" }),
    icon: Target,
  },
  subdomain: {
    parentId: "account-setup",
    label: "Set subdomain",
    href: "/settings",
    scrollTo: "subdomain-config",
    icon: Globe,
  },
  "team-members": {
    parentId: "account-setup",
    label: "Invite team members",
    href: "/settings/members",
    scrollTo: "team-members",
    icon: Users,
  },
  "meta-ads": {
    parentId: "account-setup",
    label: "Connect Meta Ad account",
    href: integrationDetailHref("meta-ads"),
    icon: Megaphone,
  },
  branding: {
    parentId: "account-setup",
    label: "Configure Design System",
    href: "/settings/brand",
    scrollTo: "brand-guidelines",
    icon: Palette,
  },
  reviews: {
    parentId: "account-setup",
    label: "Connect Reviews & Popups",
    href: integrationsHref({ tab: "reviews" }),
    icon: MessageSquareQuote,
  },
  "ai-skills": {
    parentId: "account-setup",
    label: "Upload Brand Skills",
    href: "/settings/agent",
    scrollTo: "ai-skills",
    icon: Sparkles,
  },
  "shopify-analytics": {
    parentId: "account-setup",
    label: "Enable Shopify Analytics",
    href: integrationDetailHref("shopify-analytics"),
    icon: BarChart3,
  },
  "first-funnel": {
    parentId: "first-creating",
    label: "Create your first funnel",
    href: "/funnels",
    scrollTo: "create-funnel",
    icon: Filter,
  },
  "first-experiment": {
    parentId: "first-creating",
    label: "Create your first experiment",
    href: "/experiments",
    scrollTo: "create-experiment",
    icon: FlaskConical,
  },
  "first-smart-link": {
    parentId: "first-creating",
    label: "Create your first smart link",
    href: "/smart-links",
    scrollTo: "create-smart-link",
    icon: Link2,
  },
};

export const GETTING_STARTED_TASKS: readonly GettingStartedTask[] =
  GETTING_STARTED_TASK_IDS.map((id) => ({
    id,
    ...GETTING_STARTED_TASKS_BY_ID[id],
  }));

export const gettingStartedTaskHref = ({
  href,
  scrollTo,
}: Pick<GettingStartedTask, "href" | "scrollTo">) =>
  scrollTo ? `${href}#${scrollTo}` : href;

export const JURNI_CORE_TASK_ID: GettingStartedTaskId = "jurni-core-script";

export const JURNI_CORE_REQUIRED_INFO =
  "Required for Jurni to work. Jurni Core collects your visitor, conversion and revenue data. Until it's active, metrics, experiments and attribution won't update.";
